import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react'; // 👈 QR code library component
 import ApiKeyManager from './ApiKeyManager';

// ==================== 2. MASTER APP COMPONENT ====================
function App() {
  // Authentication States
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // 🔥 Active Tab State updated (Options: 'link', 'send', or 'api')
  const [activeTab, setActiveTab] = useState('link');

  // Message Credits Tracking
  const [remainingCredits, setRemainingCredits] = useState(0);

  // WhatsApp Message States
  const [mobile, setMobile] = useState('');
  const [msg, setMsg] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  // WhatsApp Device Link States
  const [deviceConnected, setDeviceConnected] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [fetchingQr, setFetchingQr] = useState(false);

  // Common Configuration
  //const API_BASE_URL = 'https://localhost:7036/api';
    const API_BASE_URL = 'https://wapicore.mohdwebsolution.in/api';
  const API_KEY = 'fbc13b72afc54a31884912298f674b4a';

  // --- Login Handler ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/Auth/login`, {
        Username: username,
        Password: password
      });

      if (response.data.success) {
        setIsLoggedIn(true);
        setLoginError('');
        
        const credits = response.data.remainingCredits ?? 500;
        setRemainingCredits(credits);
        
        localStorage.setItem('token', response.data.token); 
        localStorage.setItem('userId', response.data.id);
      }
    } catch (error) {
      console.error('Login Error:', error);
      const errorMsg = error.response?.data?.message || 'Error connecting to .NET Core Auth Server.';
      setLoginError(errorMsg);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
    setRemainingCredits(0);
    localStorage.removeItem('token');
  };

  const handleDisconnectDevice = async () => {
    if (!window.confirm("Are you sure you want to disconnect this WhatsApp account?")) return;
    
    setFetchingQr(true);
    try {
      const savedToken = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/WhatsAppAuth/logout-device`, {}, {
        headers: { 
          'X-API-KEY': API_KEY,
          'Authorization': savedToken ? `Bearer ${savedToken}` : ''
        }
      });

      if (response.data.success) {
        setDeviceConnected(false);
        setQrCode('');
        checkDeviceStatus(); 
      }
    } catch (error) {
      console.error('Error disconnecting device:', error);
      alert("Failed to disconnect device. Try again.");
    } finally {
      setFetchingQr(false);
    }
  };

  const checkDeviceStatus = async () => {
    if (!isLoggedIn) return;
    setFetchingQr(true);
    try {
      const savedToken = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/WhatsAppAuth/get-qr?userId=savedUserId`, {
        headers: { 
          'X-API-KEY': API_KEY,
          'Authorization': savedToken ? `Bearer ${savedToken}` : ''
        }
      });
      
      if (response.data.connected) {
        setDeviceConnected(true);
        setQrCode('');
      } else if (response.data.qrString) {
        setDeviceConnected(false);
        setQrCode(response.data.qrString);
      }
    } catch (error) {
      console.error('Error fetching device status:', error);
      if (error.response?.status === 401) {
        console.warn('Session has expired.');
      }
    } finally {
      setFetchingQr(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn && activeTab === 'link') {
      checkDeviceStatus();
      const interval = setInterval(checkDeviceStatus, 10000); 
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, activeTab]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!mobile || !msg) {
      setStatus({ type: 'error', text: 'Please fill in Mobile and Message fields.' });
      return;
    }

    if (remainingCredits <= 0) {
      setStatus({ type: 'error', text: 'Insufficient message credits! Please recharge your account.' });
      return;
    }

    setLoading(true);
    setStatus({ type: '', text: '' });

    try {
      const savedToken = localStorage.getItem('token');
      const payload = {
        Mobile: mobile,
        Message: msg,
        ImageUrl: imageUrl.trim() !== '' ? imageUrl.trim() : null
      };

      const response = await axios.post(`${API_BASE_URL}/WhatsApp/send-message`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': API_KEY,
          'Authorization': savedToken ? `Bearer ${savedToken}` : ''
        }
      });

      if (response.data.success) {
        setStatus({ type: 'success', text: 'Message triggered successfully in background!' });
        setMsg('');
        setImageUrl('');
        
        if (response.data.remainingCredits !== undefined) {
          setRemainingCredits(response.data.remainingCredits);
        } else {
          setRemainingCredits(prev => Math.max(0, prev - 1));
        }
      } else {
        setStatus({ type: 'error', text: response.data.message || 'Failed to send.' });
      }
    } catch (error) {
      console.error('API Error:', error);
      setStatus({ type: 'error', text: error.response?.data?.message || 'Error connecting to .NET Core Server.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div style={styles.loginContainer}>
        <div style={styles.loginCard}>
          <h2 style={{ marginBottom: '10px', color: '#075E54' }}>WhatsApp API Gateway</h2>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>Sign in to manage your automated messages</p>
          
          <form onSubmit={handleLogin}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Username</label>
              <input 
                type="text" 
                placeholder="Enter admin username" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
                style={styles.input}
                required
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <input 
                type="password" 
                placeholder="Enter password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                style={styles.input}
                required
              />
            </div>
            {loginError && <p style={styles.errorText}>{loginError}</p>}
            <button type="submit" style={styles.loginButton}>Login to Dashboard</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.dashboardLayout}>
      
      {/* SIDEBAR NAVIGATION CONTROLS */}
      <div style={styles.sidebar}>
        <div style={{ padding: '20px', textAlign: 'center', borderBottom: '1px solid #128C7E' }}>
          <h3 style={{ color: '#fff', margin: 0 }}>Panel Controls</h3>
          <span style={{ fontSize: '12px', color: '#d1f4e0' }}>Welcome, {username}</span>
          <div style={styles.creditBadge}>
            🪙 Credits: {remainingCredits} Left
          </div>
        </div>
        
        <div style={styles.sidebarMenu}>
          <button 
            onClick={() => setActiveTab('link')} 
            style={{...styles.menuButton, backgroundColor: activeTab === 'link' ? '#128C7E' : 'transparent'}}
          >
            🔌 Link WA Device
          </button>
          <button 
            onClick={() => setActiveTab('send')} 
            style={{...styles.menuButton, backgroundColor: activeTab === 'send' ? '#128C7E' : 'transparent'}}
          >
            💬 Send Message
          </button>
          
          {/* 🔥 2. SIDEBAR MEI NAYA BUTTON ACCESS */}
          <button 
            onClick={() => setActiveTab('api')} 
            style={{...styles.menuButton, backgroundColor: activeTab === 'api' ? '#128C7E' : 'transparent'}}
          >
            🔑 Generate API Key
          </button>
        </div>

        <button onClick={handleLogout} style={styles.logoutButton}>➔ Logout</button>
      </div>

      {/* MAIN LAYOUT WRAPPER AREA */}
      <div style={styles.mainContent}>
        
        {/* VIEW A: DEVICE SCANNER CONSOLE */}
        {activeTab === 'link' && (
          <div style={styles.contentCard}>
            <h2>WhatsApp Device Authentication</h2>
            <p style={{ color: '#666', marginBottom: '25px' }}>Scan the QR code below from your WhatsApp Linked Devices screen.</p>
            
            <div style={styles.statusBanner(deviceConnected)}>
              Status: {deviceConnected ? '🟢 Connected & Ready' : '🔴 Disconnected / Authentication Required'}
            </div>

            {!deviceConnected && (
              <div style={styles.qrSection}>
                {fetchingQr && <p style={{ color: '#666' }}>Fetching latest secure QR stream...</p>}
                {qrCode ? (
                  <div style={{ textAlign: 'center', marginTop: '15px' }}>
                    <div style={{ backgroundColor: '#fff', padding: '20px', display: 'inline-block', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                      <QRCodeSVG value={qrCode} size={220} includeMargin={true} level="H" />
                    </div>
                    <p style={{ fontSize: '13px', color: '#555', marginTop: '15px', fontWeight: 'bold' }}>
                      📸 Open WhatsApp &gt; Linked Devices &gt; Link a Device
                    </p>
                  </div>
                ) : (
                  !fetchingQr && <p style={{ color: '#e74c3c' }}>No QR generated.</p>
                )}
                <button onClick={checkDeviceStatus} style={styles.refreshBtn}>Manual Refresh Status</button>
              </div>
            )}

            {deviceConnected && (
              <div style={{ textAlign: 'center', padding: '30px', color: '#27ae60' }}>
                <span style={{ fontSize: '50px' }}>✓</span>
                <p style={{ fontWeight: 'bold', fontSize: '18px' }}>Your account is successfully linked!</p>
                <button onClick={handleDisconnectDevice} style={{ padding: '10px 20px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>
                  🛑 Disconnect / Unlink WhatsApp
                </button>
              </div>
            )}
          </div>
        )}

        {/* VIEW B: MESSAGE SENDER */}
        {activeTab === 'send' && (
          <div style={styles.contentCard}>
            <h2>Automated Message Console</h2>
            <form onSubmit={handleSendMessage}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Receiver Mobile Number</label>
                <input type="text" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))} style={styles.input} required />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Message Body</label>
                <textarea rows="4" value={msg} onChange={(e) => setMsg(e.target.value)} style={{ ...styles.input, height: '100px', resize: 'vertical' }} required />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Attachment Image URL</label>
                <input type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} style={styles.input} />
              </div>
              <button type="submit" disabled={loading} style={styles.submitButton(loading)}>
                {loading ? 'Sending Request...' : 'Dispatch WhatsApp Message'}
              </button>
            </form>
            {status.text && <div style={{ ...styles.statusBar, backgroundColor: status.type === 'success' ? '#d4edda' : '#f8d7da', color: status.type === 'success' ? '#155724' : '#721c24' }}>{status.text}</div>}
          </div>
        )}

        {/* 🔥 3. VIEW C: MAPPED DYNAMIC API KEY COMPONENT */}
        {activeTab === 'api' && (
            <ApiKeyManager API_BASE_URL={API_BASE_URL} API_KEY={API_KEY} />
        )}

      </div>
    </div>
  );
}

// Styles object matches exactly what you had
const styles = {
  loginContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f0f2f5', fontFamily: 'Arial, sans-serif' },
  loginCard: { backgroundColor: '#fff', padding: '40px', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px', textAlign: 'center' },
  inputGroup: { marginBottom: '20px', textAlign: 'left' },
  label: { display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px', color: '#333' },
  input: { width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ccc', boxSizing: 'border-box', fontSize: '14px' },
  loginButton: { width: '100%', padding: '12px', backgroundColor: '#075E54', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginTop: '10px' },
  errorText: { color: '#e74c3c', fontSize: '13px', margin: '10px 0', textAlign: 'left' },
  dashboardLayout: { display: 'flex', height: '100vh', backgroundColor: '#f4f6f9', fontFamily: 'Arial, sans-serif' },
  sidebar: { width: '260px', backgroundColor: '#075E54', display: 'flex', flexDirection: 'column', color: 'white' },
  sidebarMenu: { flex: 1, padding: '20px 10px' },
  menuButton: { width: '100%', padding: '12px 15px', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', marginBottom: '10px', textAlign: 'left', transition: '0.3s' },
  creditBadge: { marginTop: '12px', padding: '8px', backgroundColor: '#128C7E', borderRadius: '5px', fontSize: '13px', fontWeight: 'bold', boxShadow: 'inset 0 0 5px rgba(0,0,0,0.2)' },
  logoutButton: { padding: '15px', backgroundColor: '#05443c', color: '#ff9999', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
  mainContent: { flex: 1, padding: '40px', overflowY: 'auto' },
  contentCard: { backgroundColor: '#fff', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', maxWidth: '700px' },
  statusBanner: (connected) => ({ padding: '12px', borderRadius: '5px', backgroundColor: connected ? '#e8f5e9' : '#ffebee', color: connected ? '#2e7d32' : '#c62828', fontWeight: 'bold', marginBottom: '20px', fontSize: '14px' }),
  qrSection: { border: '1px dashed #ccc', padding: '20px', borderRadius: '8px', backgroundColor: '#fafafa', marginTop: '20px', textAlign: 'center' },
  refreshBtn: { marginTop: '15px', padding: '8px 15px', backgroundColor: '#34495e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' },
  submitButton: (loading) => ({ width: '100%', padding: '12px', backgroundColor: loading ? '#bdc3c7' : '#128C7E', color: 'white', border: 'none', borderRadius: '5px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '16px' }),
  statusBar: { marginTop: '20px', padding: '12px', borderRadius: '5px', textAlign: 'center', fontWeight: 'bold', fontSize: '14px' }
};

export default App;